import React, { useState, useEffect, useMemo } from 'react';
import { Icons } from '../components/Icons';
import { db, appId } from '../utils/firebase';
import { collection, onSnapshot, doc, updateDoc, addDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import AddDataModal from '../components/AddDataModal';
import DetailModal from '../components/DetailModal';

const SVGBarcode = ({ value }) => {
    if (!value) return null;
    const str = String(value);
    let currentX = 0;
    const rects = [];
    for (let i = 0; i < str.length; i++) {
        const charCode = str.charCodeAt(i);
        const width1 = (charCode % 3) + 1;
        const width2 = ((charCode * 2) % 3) + 1;
        rects.push(<rect key={`${i}a`} x={currentX} y="0" width={width1} height="40" fill="#fff" />);
        currentX += width1 + width2; 
    }
    return <svg viewBox={`0 0 ${currentX} 40`} className="h-full w-full bg-black border border-gray-700 p-1 rounded" preserveAspectRatio="none">{rects}</svg>;
};

const AssetInventoryView = ({ dbData, isAdmin, showToast }) => {
    const [assets, setAssets] = useState([]);
    const [isAdding, setIsAdding] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [editingAsset, setEditingAsset] = useState(null);
    const [pendingPromotion, setPendingPromotion] = useState(null);
    const [filter, setFilter] = useState('ALL');

    useEffect(() => {
        if (!appId) return;
        const unsub = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'assets'), (snap) => {
            setAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, []);

    const checkIsPromoted = (assetId) => {
        if (!assetId) return false;
        const inArtifacts = (dbData?.artifacts || []).some(a => a.assetId === assetId);
        const inIdeas = (dbData?.ideas || []).some(i => i.assetId === assetId);
        return inArtifacts || inIdeas;
    };

    // NEW: MANIFESTATION LOGIC 
    const getManifestationBadge = (asset) => {
        const isPhys = asset.physicalStatus && asset.physicalStatus !== 'NONE';
        const isDigi = asset.digitalStatus && asset.digitalStatus !== 'NONE';
        
        if (isPhys && isDigi) return <span className="bg-yellow-900/40 text-yellow-400 border border-yellow-500/50 px-1.5 py-0.5 rounded text-[8px] font-black tracking-widest uppercase shadow-[0_0_10px_rgba(234,179,8,0.4)]">MANIFESTED</span>;
        if (isDigi) return <span className="bg-purple-900/40 text-purple-400 border border-purple-500/50 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase">VIRTUAL</span>;
        if (isPhys) return <span className="bg-orange-900/40 text-orange-400 border border-orange-500/50 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase">PHYSICAL</span>;
        
        return <span className="bg-gray-900 text-gray-500 border border-gray-700 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase">CONCEPT</span>;
    };

    const executePromotion = async (targetCol, designation) => {
        if (!pendingPromotion) return;
        try {
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', targetCol), {
                title: pendingPromotion.name, 
                assetId: pendingPromotion.assetId,
                imageUrl: pendingPromotion.imageUrl || '', 
                model3dUrl: pendingPromotion.model3dUrl || '', 
                desc: pendingPromotion.notes || 'Imported from Asset Logistics.',
                designation: designation || '', 
                status: 'DRAFT', 
                timestamp: serverTimestamp()
            });

            showToast(`Promoted to Archive`, "success");
            setPendingPromotion(null);
        } catch(e) { showToast("Failed to promote", "error"); }
    };

    const handleSaveAsset = async (formData) => {
        try {
            const coll = collection(db, 'artifacts', appId, 'public', 'data', 'assets');
            if (formData.id) await updateDoc(doc(coll, formData.id), formData);
            else await addDoc(coll, { ...formData, timestamp: serverTimestamp() });
            showToast("Updated.", "success");
            setIsAdding(false); setEditingAsset(null);
        } catch (e) { showToast("Error", "error"); }
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm("Purge this asset?")) return;
        try {
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'assets', id));
            showToast("Asset purged.", "success");
        } catch (err) { showToast("Firebase Error.", "error"); }
    };

    const filteredAssets = useMemo(() => {
        return assets.filter(a => {
            if (filter === 'ALL') return true;
            return (a.assetType || '').toUpperCase() === filter;
        });
    }, [assets, filter]);

    return (
        <div className="absolute inset-0 flex flex-col bg-[#050505] overflow-hidden fade-in">
            {pendingPromotion && (
                <div className="fixed inset-0 bg-black/90 z-[6000] flex items-center justify-center p-4 backdrop-blur-md">
                    <div className="bg-[#0a0a0a] border border-green-500/50 p-8 rounded shadow-2xl max-w-sm w-full text-center space-y-4">
                        <Icons.UploadCloud size={48} className="text-green-500 mx-auto" />
                        <h3 className="text-white font-black uppercase tracking-widest text-lg">Promote Asset</h3>
                        <p className="text-gray-500 text-xs">Choose the destination for physical prop <b>{pendingPromotion.name}</b></p>
                        <div className="flex flex-col gap-2">
                            <button onClick={() => executePromotion('artifacts', 'HIST-DOOR')} className="w-full bg-purple-900/40 text-purple-400 border border-purple-900/50 py-3 text-[10px] font-bold uppercase hover:bg-purple-900 transition-all">Promote to Door Archive</button>
                            <button onClick={() => executePromotion('artifacts', 'OBJECT')} className="w-full bg-orange-900/40 text-orange-400 border border-orange-500/50 py-3 text-[10px] font-bold uppercase hover:bg-orange-900 transition-all">Promote to Artifact Archive</button>
                            <button onClick={() => setPendingPromotion(null)} className="mt-4 text-gray-600 uppercase text-[10px] font-bold hover:text-white">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col md:flex-row md:justify-between md:items-center p-6 border-b border-gray-900 shrink-0 gap-4 bg-black/50">
                <h2 className="text-xl font-bold flex items-center gap-2 text-white"><Icons.Box className="text-blue-500" /> ASSETS & LOGISTICS</h2>
                {isAdmin && <button onClick={() => { setEditingAsset(null); setIsAdding(true); }} className="bg-blue-900/20 text-blue-400 border border-blue-900/50 px-4 py-2 text-xs font-bold uppercase rounded hover:bg-blue-900 transition-all">+ NEW PROP</button>}
            </div>

            <div className="flex gap-2 px-6 py-4 border-b border-gray-900 overflow-x-auto">
                {['ALL', 'DOOR', 'PILLAR', 'PROP'].map(tab => (
                    <button key={tab} onClick={() => setFilter(tab)} className={`px-4 py-1.5 rounded text-[10px] font-bold uppercase transition-colors ${filter === tab ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-white bg-gray-900'}`}>{tab}</button>
                ))}
            </div>

            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar pb-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch">
                    {filteredAssets.map(asset => {
                        const isPromoted = checkIsPromoted(asset.assetId);
                        
                        return (
                            <div key={asset.id} onClick={() => setSelectedAsset(asset)} className="bg-[#0a0a0a] border border-gray-800 rounded shadow-xl flex flex-col hover:border-gray-600 transition-all overflow-hidden group cursor-pointer min-h-[24rem] h-full p-4">
                                
                                {asset.imageUrl ? (
                                    <div className="h-48 w-full mb-4 bg-black rounded border border-gray-800 overflow-hidden flex items-center justify-center p-2 shrink-0">
                                        <img src={asset.imageUrl} className="max-w-full max-h-full object-contain opacity-80 group-hover:opacity-100 transition-opacity" alt="" />
                                    </div>
                                ) : (
                                    <div className="h-48 w-full mb-4 bg-gray-900 rounded border border-gray-800 flex items-center justify-center shrink-0"><Icons.Box size={48} className="text-gray-700" /></div>
                                )}

                                <div className="flex flex-col gap-4 flex-1">
                                    <div className="flex justify-between items-start">
                                        <div className="pr-4 flex flex-col gap-1.5">
                                            <div className="flex gap-2 items-center">
                                                <div className="text-[9px] font-bold text-blue-500 uppercase">{asset.assetType} {'//'} {asset.assetId}</div>
                                                {getManifestationBadge(asset)}
                                            </div>
											<h3 className="font-bold text-white text-lg leading-tight">{asset.name}</h3>
                                            
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {asset.model3dUrl && <span className="bg-cyan-900/30 text-cyan-400 border border-cyan-900/50 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase inline-flex items-center gap-1 w-fit"><Icons.Box size={8} /> 3D READY</span>}
                                                {asset.videoUrl && <span className="bg-purple-900/30 text-purple-400 border border-purple-900/50 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase inline-flex items-center gap-1 w-fit"><Icons.Play size={8} /> VIDEO</span>}
                                                {asset.audioUrl && <span className="bg-pink-900/30 text-pink-400 border border-pink-900/50 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase inline-flex items-center gap-1 w-fit"><Icons.Music size={8} /> AUDIO</span>}
                                            </div>
                                        </div>
                                        {isAdmin && !isPromoted && (
                                            <button onClick={(e) => { e.stopPropagation(); setPendingPromotion(asset); }} className="p-2 bg-green-900/20 text-green-500 border border-green-900/50 rounded hover:bg-green-600 hover:text-white transition-all shrink-0" title="Promote to Archives"><Icons.UploadCloud size={16}/></button>
                                        )}
                                        {isPromoted && (
                                            <span className="bg-green-900/50 text-green-400 border border-green-500 px-2 py-1 rounded text-[8px] font-bold tracking-widest uppercase shrink-0">PROMOTED</span>
                                        )}
                                    </div>
                                    
                                    <div className="text-xs text-gray-400 leading-relaxed line-clamp-3">
                                        {asset.notes || "No condition notes logged."}
                                    </div>

                                    <div className="bg-black border border-gray-900 p-2 text-center mt-auto flex flex-col items-center justify-center">
                                        <div className="h-6 w-full max-w-[200px]"><SVGBarcode value={asset.assetId || asset.id} /></div>
                                        <div className="text-[10px] text-gray-600 font-mono mt-1">{asset.assetId}</div>
                                    </div>
                                </div>

                                {isAdmin && (
                                    <div className="pt-4 mt-4 border-t border-gray-900 bg-transparent flex justify-end gap-2 shrink-0">
                                        <button onClick={(e) => { e.stopPropagation(); setEditingAsset(asset); }} className="px-4 py-1 text-[10px] font-bold uppercase tracking-widest bg-gray-900 text-white hover:bg-gray-800 rounded transition-colors">EDIT</button>
                                        <button onClick={(e) => handleDelete(e, asset.id)} className="p-1 text-gray-700 hover:text-red-500 transition-colors bg-black rounded"><Icons.Trash2 size={14} /></button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {selectedAsset && (
                <DetailModal 
                    item={selectedAsset} 
                    type="ASSET" 
                    isAdmin={isAdmin} 
                    onClose={() => setSelectedAsset(null)} 
                    onEdit={(item) => { 
                        setSelectedAsset(null); 
                        setTimeout(() => setEditingAsset(item), 100); 
                    }} 
                />
            )}

            {(isAdding || editingAsset) && (
                <AddDataModal 
                    type="Asset" 
                    initialData={editingAsset} 
                    onClose={() => { setIsAdding(false); setEditingAsset(null); }} 
                    onSubmit={handleSaveAsset} 
                    showToast={showToast} 
                    isAdmin={isAdmin} 
                />
            )}
        </div>
    );
};

export default AssetInventoryView;